import { Link, Outlet } from 'react-router-dom';
import { Button } from '@/components/Button';
import { CookieBanner } from '@/components/CookieBanner';
import { GoogleAnalytics } from '@/components/GoogleAnalytics';
import { HexdiLogo } from '@/components/HexdiLogo';
import { useMe } from '@/lib/auth';
import { openConsentBanner } from '@/lib/consent';

const NAV_LINKS = [
  { to: '/about', label: 'About' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/library', label: 'Library' },
  { to: '/scorecard', label: 'Scorecard' },
  { to: '/cost-of-non-compliance', label: 'Enforcement' },
  { to: '/agencies', label: 'Agencies' },
  { to: '/blog', label: 'Blog' },
  { to: '/contact', label: 'Contact' },
];

export function MarketingLayout() {
  const { data: me } = useMe();
  const isAuthed = Boolean(me);
  return (
    <div className="flex min-h-screen flex-col">
      <GoogleAnalytics />
      <CookieBanner />
      <header className="border-b border-border">
        <nav className="mx-auto flex max-w-content-wide items-center justify-between px-5 py-5 md:px-10 lg:px-20">
          <Link to="/" aria-label="HEXDI home">
            <HexdiLogo />
          </Link>
          <ul className="hidden gap-8 md:flex">
            {NAV_LINKS.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className="text-body-sm text-text-secondary hover:text-text-primary"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
          <Button asChild size="sm">
            <Link to={isAuthed ? '/dashboard' : '/login'}>
              {isAuthed ? 'Dashboard' : 'Sign in'}
            </Link>
          </Button>
        </nav>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
      <SiteFooter />
    </div>
  );
}

const FOOTER_COLUMNS: Array<{
  heading: string;
  links: Array<{ label: string; to: string }>;
}> = [
  {
    heading: 'Product',
    links: [
      { label: 'How It Works', to: '/how-it-works' },
      { label: 'Pricing', to: '/pricing' },
      { label: 'Scorecard', to: '/scorecard' },
      { label: 'Agencies', to: '/agencies' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'About', to: '/about' },
      { label: 'Blog', to: '/blog' },
      { label: 'Trust', to: '/trust' },
      { label: 'Contact', to: '/contact' },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { label: 'Terms of Service', to: '/terms-of-service' },
      { label: 'Privacy Policy', to: '/privacy' },
    ],
  },
];

function SiteFooter() {
  return (
    <footer className="border-t border-border bg-surface">
      <div className="mx-auto flex max-w-content-wide flex-col gap-12 px-5 pb-10 pt-16 md:px-10 lg:px-20">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="flex flex-col gap-3 lg:col-span-5">
            <Link to="/" aria-label="HEXDI home">
              <HexdiLogo />
            </Link>
            <p className="max-w-[320px] text-body-sm text-text-secondary">
              Export compliance management built for teams that keep
              companies compliant.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:col-span-7">
            {FOOTER_COLUMNS.map((col) => (
              <div key={col.heading} className="flex flex-col gap-4">
                <p className="text-label uppercase tracking-[0.08em] text-text-secondary">
                  {col.heading}
                </p>
                <ul className="flex flex-col gap-3">
                  {col.links.map((link) => (
                    <li key={link.to}>
                      <Link
                        to={link.to}
                        className="text-body-sm text-text-primary hover:text-accent"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2 border-t border-border pt-6 md:flex-row md:items-center md:justify-between">
          <p className="text-caption text-text-secondary">
            {new Date().getFullYear()} EXIMCORE Partners, LLC. All rights
            reserved.
          </p>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-6">
            <button
              type="button"
              onClick={openConsentBanner}
              className="text-left text-caption text-text-secondary underline underline-offset-4 hover:text-accent"
            >
              Cookie settings
            </button>
            <p className="text-caption text-text-secondary">
              HEXDI Compliance Management: Empower Your Program®
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
